import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import type { TTimelineItem } from "../../types";
import { buildTrackingIntervals } from "./intervals";
import { DEFAULT_TRACKING_OPTIONS } from "../options";
import { forgetScope, trackingOptionsFor } from "../runtime/result-cache";
import { TrackingSession } from "../runtime/session";
import type { TrackingOptions, TrackingStrategies } from "../contracts";
import type { FrameRecoveryProvider } from "../recovery/contracts";

interface TrackingStrategyOptions {
  enabled?: boolean;
  strategies?: TrackingStrategies;
  nanoMode?: TrackingOptions["nanoMode"];
  nanoModelUrls?: TrackingOptions["nanoModelUrls"];
  /** Explicitly rerun the active settings without changing strategies. */
  runKey?: number;
  recoveryEnabled?: boolean;
  recoveryProvider?: FrameRecoveryProvider;
  trackLabels?: ReadonlyMap<string, string>;
}

/** React supplies committed inputs and observes one per-player runtime session. */
export function useAnchoredTracking(
  src: string | undefined,
  items: TTimelineItem[],
  currentTime: number,
  {
    enabled = true,
    strategies = DEFAULT_TRACKING_OPTIONS.strategies,
    nanoMode = DEFAULT_TRACKING_OPTIONS.nanoMode,
    nanoModelUrls = DEFAULT_TRACKING_OPTIONS.nanoModelUrls,
    runKey = 0,
    recoveryEnabled = true,
    recoveryProvider,
    trackLabels,
  }: TrackingStrategyOptions = {},
) {
  const { sparse, nano, hybrid } = strategies;
  const { backbone, head, wasm } = nanoModelUrls;
  const trackingEnabled = enabled && (sparse || nano || hybrid);
  const intervals = useMemo(() => (trackingEnabled ? buildTrackingIntervals(items) : []), [trackingEnabled, items]);
  // A new configuration immediately exposes an empty snapshot, before effect cleanup.
  // Constructing a session never allocates workers during render.
  const session = useMemo(
    () =>
      new TrackingSession({
        src,
        enabled: trackingEnabled,
        options: trackingOptionsFor({
          strategies: { sparse, nano, hybrid },
          nanoMode,
          nanoModelUrls: { backbone, head, wasm },
        }),
        runKey,
      }),
    // An explicit rerun creates a fresh session.
    [src, trackingEnabled, sparse, nano, hybrid, nanoMode, backbone, head, wasm, runKey],
  );
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);
  // An explicit rerun asks for these results to be thrown away, and that has to
  // outlive this player: `runKey` is per-player state that resets on remount,
  // so the discard is what makes a rerun stick. Ordered before `start` below,
  // which is what would otherwise recall them.
  const lastRunKey = useRef(runKey);
  useEffect(() => {
    if (lastRunKey.current !== runKey) {
      lastRunKey.current = runKey;
      forgetScope(session.scope);
    }
  }, [session, runKey]);
  useEffect(
    () => session.configureRecovery({ enabled: recoveryEnabled, provider: recoveryProvider }, trackLabels),
    [session, recoveryEnabled, recoveryProvider, trackLabels],
  );
  useEffect(() => session.update(intervals, currentTime), [session, intervals, currentTime]);
  useEffect(() => session.start(), [session]);
  return { intervals, ...snapshot };
}
